import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { FiPlay, FiClock, FiChevronDown, FiChevronRight, FiLock } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiUrls, getHttpHeaders } from '../config/api';

interface PackageDetailPageProps {
  packageId: number;
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onFaqClick?: () => void;
  onFeedbackClick?: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick?: () => void;
  onMkAcademyInPersonClick?: () => void;
  onBackToPackages: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: any;
  onLogout: () => void;
}

interface ContentAsset {
  id: number;
  title: string;
  description?: string;
  duration_sec: number;
  file_url: string;
  content_type_name?: string;
  file_type?: string;
  is_preview?: boolean;
  is_published?: boolean;
}

interface StructureNode {
  id: number;
  name: string;
  description?: string;
  sort_order: number;
  level?: string;
  assets: ContentAsset[];
  children: StructureNode[];
}

interface PackageData {
  id: number;
  name: string;
  description: string;
  price: number;
  discount_price?: number;
  duration_months: number;
  structureTree: StructureNode[];
  isSubscribed: boolean;
  total_videos: number;
}

const PackageDetailPage: React.FC<PackageDetailPageProps> = (props) => {
  const [packageData, setPackageData] = useState<PackageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentVideo, setCurrentVideo] = useState<ContentAsset | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const [currentVideoType, setCurrentVideoType] = useState<'s3' | 'youtube' | 'unknown'>('unknown');
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  
  // Video progress tracking
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastProgressUpdateRef = useRef<number>(0);
  const progressUpdateIntervalRef = useRef<number>(10); // Update every 10 seconds

  // Function to send video progress to analytics service
  const updateVideoProgress = useCallback(async (
    watchDuration: number, 
    totalDuration: number, 
    isCompleted: boolean = false
  ) => {
    if (!props.user?.id || !currentVideo?.id) return;
    
    try {
      const ANALYTICS_SERVICE_URL = process.env.REACT_APP_ANALYTICS_SERVICE_URL || 'http://localhost:3016';
      
      await fetch(`${ANALYTICS_SERVICE_URL}/api/progress/update-video`, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify({
          user_id: props.user.id,
          video_id: currentVideo.id,
          package_id: props.packageId,
          watch_duration: Math.floor(watchDuration),
          total_duration: Math.floor(totalDuration),
          is_completed: isCompleted
        })
      });
      
      console.log('📊 Video progress updated:', { 
        videoId: currentVideo.id, 
        watchDuration: Math.floor(watchDuration), 
        isCompleted 
      });
    } catch (error) {
      console.error('Failed to update video progress:', error);
    }
  }, [props.user?.id, currentVideo?.id, props.packageId]);

  // Handle video time updates (throttled)
  const handleVideoTimeUpdate = useCallback(() => {
    if (!videoRef.current) return;
    
    const currentTime = videoRef.current.currentTime;
    const duration = videoRef.current.duration;
    
    // Only update every N seconds to avoid too many API calls
    if (currentTime - lastProgressUpdateRef.current >= progressUpdateIntervalRef.current) {
      lastProgressUpdateRef.current = currentTime;
      updateVideoProgress(currentTime, duration, false);
    }
  }, [updateVideoProgress]);

  // Handle video ended
  const handleVideoEnded = useCallback(() => {
    if (!videoRef.current) return;
    
    const duration = videoRef.current.duration;
    updateVideoProgress(duration, duration, true);
    console.log('🎉 Video completed!');
  }, [updateVideoProgress]);

  // Reset progress tracking when video changes
  useEffect(() => {
    lastProgressUpdateRef.current = 0;
  }, [currentVideo?.id]);

  interface VideoSelection {
    asset: ContentAsset;
    path: string[];
    nodeIds: number[];
  }

  const countNodeVideos = (node: StructureNode): number => {
    const childCount = node.children.reduce((sum, child) => sum + countNodeVideos(child), 0);
    return node.assets.length + childCount;
  };

  const findFirstAvailableVideo = (
    nodes: StructureNode[],
    isSubscribed: boolean,
    parentPath: string[] = [],
    parentIds: number[] = []
  ): VideoSelection | null => {
    for (const node of nodes) {
      const nodePath = [...parentPath, node.name];
      const nodeIds = [...parentIds, node.id];

      const asset = node.assets.find(asset => isSubscribed || asset.is_published !== false);
      if (asset) {
        return { asset, path: nodePath, nodeIds };
      }

      const childResult = findFirstAvailableVideo(node.children, isSubscribed, nodePath, nodeIds);
      if (childResult) {
        return childResult;
      }
    }
    return null;
  };

  const loadVideoForAsset = async (asset: ContentAsset, isSubscribed: boolean) => {
    setVideoLoading(true);

    if (!asset) {
      setCurrentVideoUrl(null);
      setCurrentVideoType('unknown');
      setVideoError(null);
      setVideoLoading(false);
      return;
    }

    if (!isSubscribed && asset.is_published === false && !asset.is_preview) {
      // Locked for non-subscribed users
      setCurrentVideoUrl(null);
      setCurrentVideoType('unknown');
      setVideoError(null);
      setVideoLoading(false);
      return;
    }

    setVideoError(null);
    setCurrentVideoUrl(null);
    setCurrentVideoType('unknown');

    console.log('🎬 Loading asset', {
      id: asset.id,
      title: asset.title,
      file_url: asset.file_url,
      is_published: asset.is_published,
      is_preview: asset.is_preview,
      isSubscribed
    });

    if (!asset.id) {
      console.warn('⚠️ Asset missing ID, cannot fetch signed URL', asset);
      setVideoError('Video metadata incomplete.');
      setVideoLoading(false);
      return;
    }

    let fileUrl = asset.file_url || '';
    let fileTypeFromAsset = asset.file_type || asset.content_type_name;

    if (!fileUrl) {
      console.log('ℹ️ Asset missing file_url, fetching detail for asset', asset.id);
      try {
        const detailResponse = await fetch(apiUrls.content.byId(asset.id), {
          headers: getHttpHeaders()
        });

        if (detailResponse.ok) {
          const detailData = await detailResponse.json();
          const detail = detailData.content || detailData;
          fileUrl = detail.file_url || detail.s3_key || detail.url || fileUrl;
          fileTypeFromAsset = detail.file_type || fileTypeFromAsset;
          console.log('ℹ️ Asset detail fetch succeeded', { fileUrl, fileTypeFromAsset });
        } else {
          const text = await detailResponse.text();
          console.warn('⚠️ Asset detail fetch failed', detailResponse.status, text);
        }
      } catch (detailError) {
        console.warn('⚠️ Error fetching asset detail', detailError);
      }
    }

    // If the URL is already absolute (YouTube or external), use it directly
    if (fileUrl && fileUrl.startsWith('http')) {
      setCurrentVideoUrl(fileUrl);
      setCurrentVideoType(
        fileUrl.includes('youtube') || fileTypeFromAsset === 'youtube' ? 'youtube' : 's3'
      );
      setVideoLoading(false);
      return;
    }

    try {
      const signedUrlEndpoint = `${apiUrls.content.byId(asset.id)}/signed-url`;
      console.log('🔑 Fetching signed URL:', signedUrlEndpoint);

      const response = await fetch(signedUrlEndpoint, {
        headers: getHttpHeaders()
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('❌ Failed to fetch signed URL', response.status, text);
        throw new Error(`Signed URL request failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Signed URL response:', data);

      const signedUrl = data.signedUrl || data.url || data.playbackUrl;
      const fileType = data.fileType || data.type || fileTypeFromAsset;

      if (signedUrl) {
        setCurrentVideoUrl(signedUrl);
        setCurrentVideoType(fileType === 'youtube' || signedUrl.includes('youtube') ? 'youtube' : 's3');
      } else {
        console.warn('⚠️ Signed URL missing in response');
        setVideoError('Signed video URL not available.');
        setCurrentVideoUrl(null);
        setCurrentVideoType('unknown');
      }
    } catch (err) {
      console.error('❌ Failed to load signed URL for asset', asset.id, err);
      setVideoError('Unable to load this video right now.');
      setCurrentVideoUrl(null);
      setCurrentVideoType('unknown');
    } finally {
      setVideoLoading(false);
    }
  };

  useEffect(() => {
    if (props.packageId) {
      fetchPackageDetails();
    }
  }, [props.packageId]);

  useEffect(() => {
    if (currentVideo) {
      loadVideoForAsset(currentVideo, packageData?.isSubscribed ?? false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageData?.isSubscribed]);

  const fetchPackageDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch package info
      const packageResponse = await fetch(`${apiUrls.packages.byId(props.packageId)}`, {
        headers: getHttpHeaders()
      });

      if (!packageResponse.ok) {
        throw new Error('Failed to fetch package details');
      }

      const packageInfo = await packageResponse.json();
      console.log('📦 Package info:', packageInfo);

      // Check subscription status first
      const isSubscribed = props.isAuthenticated && props.user?.id 
        ? await checkSubscription(props.packageId)
        : false;

      // Build hierarchical structure tree
      const structureTree = await transformStructuresToTree(packageInfo.package, isSubscribed);

      const packageData: PackageData = {
        id: packageInfo.package.id,
        name: packageInfo.package.name,
        description: packageInfo.package.description,
        price: parseFloat(packageInfo.package.annual_price || packageInfo.package.price || 0),
        discount_price: packageInfo.package.discount_price ? parseFloat(packageInfo.package.discount_price) : undefined,
        duration_months: packageInfo.package.duration_months || 12,
        structureTree,
        isSubscribed,
        total_videos: structureTree.reduce((sum, node) => sum + countNodeVideos(node), 0)
      };

      setPackageData(packageData);

      const firstSelection = findFirstAvailableVideo(structureTree, isSubscribed);
      const initialExpanded = new Set<number>(structureTree.map(node => node.id));

      if (firstSelection) {
        setCurrentVideo(firstSelection.asset);
        setCurrentPath(firstSelection.path);
        firstSelection.nodeIds.forEach(id => initialExpanded.add(id));
        await loadVideoForAsset(firstSelection.asset, isSubscribed);
      }

      setExpandedNodes(initialExpanded);

    } catch (err) {
      console.error('Error fetching package details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load package');
    } finally {
      setLoading(false);
    }
  };

  const transformStructuresToTree = async (packageInfo: any, isSubscribed: boolean): Promise<StructureNode[]> => {
    try {
      console.log('📦 Full package info:', packageInfo);

      let structures = packageInfo.packageStructures || packageInfo.structures || [];
      console.log('🏗️ Direct package structures:', structures.length);

      if (structures.length === 0 && packageInfo.packageBooks) {
        console.log('📚 No direct structures, fetching through books...');
        const fetchedStructures = new Map<number, any>();

        for (const packageBook of packageInfo.packageBooks) {
          const bookId = packageBook.book_id || packageBook.book?.id;
          if (!bookId) continue;

          try {
            const response = await fetch(
              `${apiUrls.contentLibrary.contentStructures}/book-content-links?book_id=${bookId}`,
              { headers: getHttpHeaders() }
            );

            if (response.ok) {
              const data = await response.json();
              const links = data.links || data || [];

              for (const link of links) {
                const structureId = link.structure_id;
                if (!structureId || fetchedStructures.has(structureId)) {
                  continue;
                }

                const structResponse = await fetch(
                  `${apiUrls.contentLibrary.contentStructures}/${structureId}`,
                  { headers: getHttpHeaders() }
                );

                if (structResponse.ok) {
                  const structData = await structResponse.json();
                  fetchedStructures.set(structureId, {
                    structure_id: structureId,
                    structure: structData.structure || structData
                  });
                }
              }
            }
          } catch (err) {
            console.warn(`  ⚠️ Failed to fetch structures for book ${bookId}:`, err);
          }
        }

        structures = Array.from(fetchedStructures.values());
      }

      if (structures.length === 0) {
        console.warn('⚠️ No content structures found for package');
        return [];
      }

      const structureMap = new Map<number, any>();
      structures.forEach((s: any) => {
        const struct = s.structure || s;
        const id = s.structure_id || s.id || struct.id;
        if (id && !structureMap.has(id)) {
          structureMap.set(id, s);
        }
      });

      const rootCandidates = Array.from(structureMap.values());
      const mainRoot = rootCandidates.find((s: any) => {
        const name = ((s.structure || s).name || '').trim();
        return name.length > 0 && !name.match(/^[st]\d+/i) && !name.match(/^[st]\d+-/i);
      });

      const rootsToProcess = mainRoot ? [mainRoot] : rootCandidates;

      const fetchAssetsForStructure = async (structureId: number, fallbackName: string): Promise<ContentAsset[]> => {
        try {
          const response = await fetch(
            `${apiUrls.contentLibrary.contentStructures}/${structureId}/content-links`,
            { headers: getHttpHeaders() }
          );

          if (!response.ok) {
            return [];
          }

          const assetsData = await response.json();
          let assetLinks: any[] = [];

          if (Array.isArray(assetsData)) {
            assetLinks = assetsData;
          } else if (assetsData.links) {
            assetLinks = Array.isArray(assetsData.links) ? assetsData.links : [assetsData.links];
          } else if (assetsData.data) {
            assetLinks = Array.isArray(assetsData.data) ? assetsData.data : [assetsData.data];
          } else if (assetsData.content_asset_id) {
            assetLinks = [assetsData];
          }

          const seenIds = new Set<number>();
          const assets: ContentAsset[] = [];

          for (const link of assetLinks) {
            const assetId = link.content_asset_id || link.id;
            const assetInfo = link.asset || link;

            if (!assetId || seenIds.has(assetId)) {
              continue;
            }
            seenIds.add(assetId);

            const asset: ContentAsset = {
              id: assetId,
              title: assetInfo?.title || fallbackName || 'Video',
              description: assetInfo?.description || '',
              duration_sec: assetInfo?.duration_sec || 0,
          file_url:
            assetInfo?.file_url ||
            assetInfo?.s3_key ||
            assetInfo?.url ||
            link.file_url ||
            link.s3_key ||
            '',
              content_type_name: assetInfo?.content_type_name || 'Video',
        file_type: assetInfo?.file_type || assetInfo?.storageType,
              is_preview: assetInfo?.is_preview ?? false,
              is_published: assetInfo?.is_published ?? true
            };

            assets.push(asset);
          }

          return assets;
        } catch (err) {
          console.warn(`  ⚠️ Failed to fetch assets for structure ${structureId}:`, err);
          return [];
        }
      };

      const buildNode = async (structure: any, depth: number = 0): Promise<StructureNode | null> => {
        const indent = '  '.repeat(depth);
        const structData = structure.structure || structure;
        const nodeId = structure.structure_id || structure.id || structData.id;

        if (!nodeId) {
          console.warn(`${indent}⚠️ Structure without ID skipped`, structure);
          return null;
        }

        console.log(`${indent}📂 Building node "${structData.name}" (${nodeId})`);

        let children: StructureNode[] = [];

        try {
          const response = await fetch(
            `${apiUrls.contentLibrary.contentStructures}?parent_id=${nodeId}`,
            { headers: getHttpHeaders() }
          );

          if (response.ok) {
            const data = await response.json();
            const rawChildren = data.structures || [];

            for (const child of rawChildren) {
              const childNode = await buildNode(child, depth + 1);
              if (childNode) {
                children.push(childNode);
              }
            }
          }
        } catch (err) {
          console.warn(`${indent}⚠️ Failed to fetch children for ${nodeId}:`, err);
        }

        const assets = await fetchAssetsForStructure(nodeId, structData.name || `Structure ${nodeId}`);

        const node: StructureNode = {
          id: nodeId,
          name: structData.name || `Structure ${nodeId}`,
          description: structData.description || '',
          sort_order: structData.sort_order ?? 0,
          level: structData.level || structData.structure_level,
          assets,
          children: children.sort((a, b) => a.sort_order - b.sort_order)
        };

        return node;
      };

      const tree: StructureNode[] = [];
      for (const root of rootsToProcess) {
        const node = await buildNode(root, 0);
        if (node) {
          tree.push(node);
        }
      }

      return tree.sort((a, b) => a.sort_order - b.sort_order);
    } catch (error) {
      console.error('❌ Error transforming structures to tree:', error);
      return [];
    }
  };

  const checkSubscription = async (packageId: number): Promise<boolean> => {
    try {
      if (!props.user?.id) return false;

      const response = await fetch(`${apiUrls.subscriptions.byUser(props.user.id)}`, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        return data.subscriptions?.some((sub: any) =>
          sub.package_id === packageId && sub.status === 'active'
        ) || false;
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
    return false;
  };

  const toggleNode = (nodeId: number) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleVideoSelect = async (asset: ContentAsset, path: string[]) => {
    setCurrentVideo(asset);
    setCurrentPath(path);

    await loadVideoForAsset(asset, packageData?.isSubscribed ?? false);

    if (!packageData?.isSubscribed && asset.is_published === false && !asset.is_preview) {
      console.log('🔒 Locked video selected by non-subscriber (unpublished content)');
    } else {
      console.log('▶️ Ready to play video:', asset.title);
    }
  };

  const renderVideoContent = () => {
    if (!currentVideo) {
      return null;
    }

    if (videoLoading) {
      return (
        <VideoFallbackContainer>
          <VideoFallbackTitle>Loading video…</VideoFallbackTitle>
          <VideoFallbackMessage>Please wait a moment.</VideoFallbackMessage>
        </VideoFallbackContainer>
      );
    }

    if (!currentVideoUrl) {
      return (
        <VideoFallbackContainer>
          <VideoFallbackTitle>Video unavailable</VideoFallbackTitle>
          <VideoFallbackMessage>
            {videoError || 'We were unable to load this video. Please try again later.'}
          </VideoFallbackMessage>
        </VideoFallbackContainer>
      );
    }

    if (currentVideoType === 'youtube') {
      return (
        <YouTubeFrame
          key={currentVideoUrl}
          src={currentVideoUrl}
          title={currentVideo.title || 'Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    return (
      <StyledVideo
        ref={videoRef}
        controls
        preload="metadata"
        playsInline
        key={currentVideoUrl}
        onTimeUpdate={handleVideoTimeUpdate}
        onEnded={handleVideoEnded}
        onPause={() => {
          // Save progress when paused
          if (videoRef.current) {
            updateVideoProgress(
              videoRef.current.currentTime, 
              videoRef.current.duration, 
              false
            );
          }
        }}
      >
        <source src={currentVideoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </StyledVideo>
    );
  };

  const renderStructureNode = (
    node: StructureNode,
    depth: number = 0,
    parentPath: string[] = []
  ): React.ReactNode => {
    const nodePath = [...parentPath, node.name];
    const hasChildren = node.children.length > 0;
    const hasAssets = node.assets.length > 0;
    const isHeaderClickable = hasChildren || hasAssets;
    const isExpanded = expandedNodes.has(node.id);
    const totalVideos = countNodeVideos(node);

    return (
      <React.Fragment key={`node-${node.id}`}>
        <TreeNodeRow
          onClick={() => isHeaderClickable && toggleNode(node.id)}
          $depth={depth}
          $expanded={isExpanded}
          $clickable={isHeaderClickable}
        >
          <TreeNodePrefix>
            {isHeaderClickable ? (
              isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />
            ) : (
              <TreeDot />
            )}
          </TreeNodePrefix>
          <TreeNodeLabel title={node.name}>{node.name}</TreeNodeLabel>
          <TreeNodeMeta>{totalVideos} videos</TreeNodeMeta>
        </TreeNodeRow>

        {isExpanded && (
          <>
            {node.assets.map(asset => {
              const isLocked =
                !packageData?.isSubscribed &&
                asset.is_published === false &&
                !asset.is_preview;
              const isCurrent = currentVideo?.id === asset.id;

              return (
                <AssetRow
                  key={`asset-${node.id}-${asset.id}`}
                  onClick={() => { void handleVideoSelect(asset, nodePath); }}
                  $depth={depth + 1}
                  $isLocked={isLocked}
                  $isCurrent={isCurrent}
                >
                  <AssetLeft>
                    {isCurrent ? (
                      <CurrentIcon>▶</CurrentIcon>
                    ) : isLocked ? (
                      <LockIconWrapper><FiLock size={13} /></LockIconWrapper>
                    ) : (
                      <TreeDot />
                    )}
                    <AssetTitle title={asset.title || node.name}>
                      {asset.title || node.name}
                      {asset.is_preview && <AssetTag>Preview</AssetTag>}
                      {!packageData?.isSubscribed && asset.is_published !== false && (
                        <AssetTag $variant="published">Published</AssetTag>
                      )}
                      {isLocked && <AssetTag $variant="locked">Locked</AssetTag>}
                    </AssetTitle>
                  </AssetLeft>
                  <AssetDuration>
                    <FiClock size={12} />
                    {asset.duration_sec ? formatDuration(asset.duration_sec) : '00:00'}
                  </AssetDuration>
                </AssetRow>
              );
            })}

            {node.children.map(child =>
              renderStructureNode(child, depth + 1, nodePath)
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <Header {...props} />
        <LoadingContainer>
          <LoadingSpinner />
          <p>Loading package details...</p>
        </LoadingContainer>
        <Footer {...props} />
      </PageContainer>
    );
  }

  if (error || !packageData) {
    return (
      <PageContainer>
        <Header {...props} />
        <ErrorContainer>
          <h2>📦 Package Not Found</h2>
          <p>{error || 'Unable to load package details'}</p>
          <BackButton onClick={props.onBackToPackages}>
            ← Back to Self-Learning Hub
          </BackButton>
        </ErrorContainer>
        <Footer {...props} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header {...props} />

      <ContentContainer>
        {/* Package Title */}
        <PageHeader>
          <BackLink onClick={props.onBackToPackages}>
            ← Back to Packages
          </BackLink>
          <PackageTitle>{packageData.name}</PackageTitle>
        </PageHeader>

        <MainGrid>
          {/* Left: Video Player */}
          <VideoSection>
            {currentVideo ? (
              <>
                <VideoPlayer>
                  {packageData.isSubscribed ||
                  currentVideo.is_preview ||
                  currentVideo.is_published !== false ? (
                    renderVideoContent()
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                      borderRadius: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      textAlign: 'center',
                      padding: '40px'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        background: 'rgba(59, 130, 246, 0.2)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '24px'
                      }}>
                        <FiLock size={40} color="#3b82f6" />
                      </div>
                      <h3 style={{ fontSize: '20px', marginBottom: '12px', fontWeight: '600' }}>
                        🔒 Premium Content
                      </h3>
                      <p style={{ fontSize: '15px', color: '#9ca3af', marginBottom: '24px', maxWidth: '300px' }}>
                        Subscribe to unlock unpublished lessons and {packageData.total_videos}+ total videos
                      </p>
                      <button
                        onClick={() => alert('Subscribe functionality - integrate with checkout')}
                        style={{
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px 32px',
                          fontSize: '16px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        🎯 Subscribe Now - ${packageData.discount_price || packageData.price}/month
                      </button>
                    </div>
                  )}
                </VideoPlayer>

                <VideoInfo>
                  <CurrentLabel>
                    <FiPlay size={16} />
                    Current Explanation
                  </CurrentLabel>
                  <VideoTitle>{currentVideo.title}</VideoTitle>
                  {currentVideo && (
                    <WeekDay>
                      {[...currentPath, currentVideo.title || 'Video'].join(' › ')}
                    </WeekDay>
                  )}
                  {currentVideo.description && (
                    <VideoDescription>{currentVideo.description}</VideoDescription>
                  )}
                </VideoInfo>
              </>
            ) : (
              <EmptyState>
                <FiPlay size={64} />
                <p>Select a video from the playlist to start learning</p>
              </EmptyState>
            )}
          </VideoSection>

          {/* Right: Playlist */}
          <PlaylistSection>
            <PlaylistHeader>
              <PlaylistIcon>≡</PlaylistIcon>
              <h3>Explanation Playlist</h3>
            </PlaylistHeader>

            <TreeList>
              {packageData.structureTree.map(node => renderStructureNode(node))}
            </TreeList>

            {!packageData.isSubscribed && (
              <SubscribeFooter>
                <SubscribeButton onClick={() => {
                  alert('Subscribe functionality - integrate with your checkout flow');
                }}>
                  🎯 Subscribe to Access All Videos
                </SubscribeButton>
                <SubscribeInfo>
                  ${packageData.discount_price || packageData.price}/month • {packageData.total_videos} videos • {packageData.duration_months} months access
                </SubscribeInfo>
              </SubscribeFooter>
            )}
          </PlaylistSection>
        </MainGrid>
      </ContentContainer>

      <Footer {...props} />
    </PageContainer>
  );
};

// Helper function
const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default PackageDetailPage;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 140px 20px 60px;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 12px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }
`;

const PackageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const VideoSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const VideoPlayer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background: #000;
`;

const YouTubeFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 12px;
  background: #000;
`;

const VideoFallbackContainer = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  padding: 40px;
`;

const VideoFallbackTitle = styled.h3`
  font-size: 20px;
  margin-bottom: 12px;
  font-weight: 600;
`;

const VideoFallbackMessage = styled.p`
  font-size: 15px;
  color: #d1d5db;
  margin: 0;
  max-width: 320px;
`;

const VideoInfo = styled.div`
  padding: 16px 0;
`;

const CurrentLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;

  svg {
    color: #3b82f6;
  }
`;

const VideoTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
`;

const WeekDay = styled.div`
  font-size: 14px;
  color: #3b82f6;
  font-weight: 600;
  margin-bottom: 12px;
`;

const VideoDescription = styled.p`
  font-size: 15px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #9ca3af;

  svg {
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    font-size: 16px;
  }
`;

const PlaylistSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  max-height: 800px;
  display: flex;
  flex-direction: column;
`;

const PlaylistHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 16px;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }
`;

const PlaylistIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #fef3c7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #f59e0b;
  font-weight: 700;
`;

const TreeList = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

const TreeNodeRow = styled.div<{ $depth: number; $expanded: boolean; $clickable: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  margin-bottom: 4px;
  border-radius: 10px;
  position: relative;
  padding-left: ${props => 20 + props.$depth * 24}px;
  background: ${props => props.$expanded ? '#eef2ff' : 'transparent'};
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s;
  border: 1px solid transparent;

  &:hover {
    background: #eef2ff;
    border-color: rgba(59, 130, 246, 0.25);
  }

  &:before {
    content: '';
    position: absolute;
    left: ${props => 10 + props.$depth * 24}px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(148, 163, 184, 0.35);
    opacity: ${props => props.$depth === 0 ? 0 : 1};
  }
`;

const TreeNodePrefix = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  color: #64748b;
  flex-shrink: 0;
`;

const TreeDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #94a3b8;
`;

const TreeNodeLabel = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 2px;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.5);
    border-radius: 999px;
  }
`;

const TreeNodeMeta = styled.div`
  font-size: 12px;
  color: #64748b;
  background: rgba(148, 163, 184, 0.15);
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 500;
`;

const AssetRow = styled.div<{ $depth: number; $isLocked: boolean; $isCurrent: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 14px;
  margin: 2px 0;
  border-radius: 8px;
  position: relative;
  padding-left: ${props => 32 + props.$depth * 24}px;
  cursor: pointer;
  background: ${props => props.$isCurrent ? '#edf2ff' : 'transparent'};
  border: 1px solid ${props => props.$isCurrent ? 'rgba(59, 130, 246, 0.35)' : 'transparent'};
  opacity: ${props => props.$isLocked && !props.$isCurrent ? 0.7 : 1};
  transition: all 0.2s;

  &:before {
    content: '';
    position: absolute;
    left: ${props => 22 + props.$depth * 24}px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(148, 163, 184, 0.25);
  }

  &:hover {
    background: #f8fafc;
    border-color: rgba(59, 130, 246, 0.2);
  }
`;

const AssetLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const CurrentIcon = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const LockIconWrapper = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.08);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AssetTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.45);
    border-radius: 999px;
  }
`;

const AssetTag = styled.span<{ $variant?: 'locked' | 'published' }>`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  color: ${props => {
    if (props.$variant === 'locked') return '#b91c1c';
    if (props.$variant === 'published') return '#2563eb';
    return '#047857';
  }};
  background: ${props => {
    if (props.$variant === 'locked') return 'rgba(239, 68, 68, 0.18)';
    if (props.$variant === 'published') return 'rgba(59, 130, 246, 0.18)';
    return 'rgba(16, 185, 129, 0.18)';
  }};
`;

const AssetDuration = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
`;

const SubscribeFooter = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #e5e7eb;
`;

const SubscribeButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
  }
`;

const SubscribeInfo = styled.div`
  text-align: center;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;

  p {
    margin-top: 16px;
    color: #6b7280;
    font-size: 16px;
  }
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  max-width: 600px;
  margin: 100px auto;
  padding: 40px;
  background: white;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  h2 {
    color: #1f2937;
    margin-bottom: 16px;
  }

  p {
    color: #6b7280;
    margin-bottom: 24px;
  }
`;

const BackButton = styled.button`
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-size: 15px;

  &:hover {
    background: #2563eb;
  }
`;

